/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { reviewsUrl } from "@Services/dataManager";
import { useTranslation, untranslated } from "@i18n";
import type { TranslatedString } from "@i18n";
import { forwardRef, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { convertDate } from "@Services/luxonHelper";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { reviewStageMapper } from "./ReviewsConstants";
import { updateReviewStatus, getReviewsGridColumns } from "./ReviewsHelper";
import CreateReviewModal from "./CreateReviewModal";
import ReviewProperties from "./ReviewProperties";
import { getReviewPageUrl } from "@Services/utils/helpers";
import { getReviewUrl } from "@Features/projects/utils/projectUrls";

import { renderUserTag } from "@CubeComponents/html/userTagDom";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import { FaPlus } from "react-icons/fa";
import IconComponent from "@CubeComponents/icons";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS, reviewViewType } from "@Services/constants";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import { IoChevronBack } from "react-icons/io5";
import ArchiveToggleIcon from "@Assets/icons/svg/ArchiveToggleIcon.svg";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import _ from "lodash";
import { recordStatus } from "helpers/constants";
import Text from "@CubeComponents/html/Text/Text";
import { GridPageLayout } from "@CubeComponents/html/GridPageLayout";
import type { ReviewsServiceRequest, UpdateReviewStatusRequest } from "@ServerTypes/reviews/reviews.schema";
import type { ReviewsGridRow } from "./ReviewsHelper";
import type {
  GridClickEvent,
  SlickGrid1Ref,
  ToolbarItem,
  ValidatedColumnDefinition,
} from "@CubeComponents/html/SlickGrid1";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

/**
 * The grid query envelope: SlickGrid1 posts `{ value: <this> }` to `/reviewsService`, so this is
 * `reviewsServiceRequest.value` itself.
 */
type ReviewsGridQuery = NonNullable<ReviewsServiceRequest["value"]>;

/**
 * `reviewsServiceRequest.value.params` plus the one field this component adds that the schema
 * does not declare — see the note in `buildBaseQuery`.
 */
type ReviewsGridQueryParams = NonNullable<ReviewsGridQuery["params"]> & { requiresCounts?: boolean };

interface ReviewsListProps {
  setIsRowSelected?: (isSelected: boolean) => void;
  showOnlyGrid?: boolean;
  showNavigateColumn?: boolean;
  customQuery?: ReviewsGridQuery | null;
  spaceId?: string;
  isActionable?: boolean;
  /** One of the TakeAction due-date buckets: "overdue" | "next7days" | "next30days". */
  activeFilter?: string | null;
  selectedProjectIds?: string[];
  archiveToggle?: boolean;
  isPortalLevel?: boolean;
}

const ReviewsList = forwardRef(
  (
    {
      setIsRowSelected,
      showOnlyGrid = false,
      showNavigateColumn = false,
      customQuery = null,
      spaceId,
      isActionable = false,
      activeFilter,
      selectedProjectIds,
      archiveToggle = false,
      isPortalLevel = false,
    }: ReviewsListProps,
    ref,
  ) => {
    const { t } = useTranslation();
    const [urlParams] = useSearchParams();
    const projectId = urlParams.get("projectId");
    const location = useLocation();

    const tempRef = useRef<SlickGrid1Ref<ReviewsGridRow> | null>(null);
    // `ref` is a ForwardedRef<unknown> (this component intentionally publishes no ref type, so
    // callers such as forms/reviewContent.tsx may declare their own handle slice). Both branches
    // are ref objects at every call site; the assertion is erased.
    const dataGrid = (ref || tempRef) as React.MutableRefObject<SlickGrid1Ref<ReviewsGridRow> | null>;
    const [createReviewModal, setCreateReviewModal] = useState<{ show: boolean }>({ show: false });
    const [recordProperties, setRecordProperties] = useState<{ show: boolean; details: ReviewsGridRow }>({
      show: false,
      details: {},
    });
    const [hasSelection, setHasSelection] = useState(false);
    const [propertiesBtnDisabled, setPropertiesBtnDisabled] = useState<boolean>(true);
    const [reviewsQuery, setReviewsQuery] = useState<ReviewsGridQuery | null>(null);
    const [archiveModal, setArchiveModal] = useState<{ show: boolean }>({
      show: false,
    });
    const [deleteModal, setDeleteModal] = useState<{ show: boolean }>({
      show: false,
    });
    const [isLoadingReview, setIsLoadingReview] = useState(false);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const reviewType = urlParams.get("type");

    useEffect(() => {
      const handleSelect = getReviewPageUrl(reviewViewType.ACTIVE_REVIEW, urlParams.toString(), location.pathname);
      navigate(handleSelect, { replace: true });
    }, []);

    useEffect(() => {
      if (reviewType) {
        const initialQuery = buildBaseQuery(activeFilter);
        setReviewsQuery(initialQuery);
      }
    }, [spaceId, projectId, isActionable, selectedProjectIds, activeFilter, customQuery, reviewType]);

    const buildBaseQuery = (filterType: string | null = null): ReviewsGridQuery => {
      const reviewTypeFlag = reviewType === reviewViewType.ACTIVE_REVIEW ? recordStatus.ACTIVE : recordStatus.ARCHIVED;
      const params: ReviewsGridQueryParams = {};

      // Erased assertions: `spaceId` arrives as a prop and `projectId` off `useSearchParams`, both
      // plain strings, but `reviewsServiceRequest.value.params` declares them `objectIdString`.
      // Both are route-supplied 24-hex ids; the values sent are unchanged.
      if (spaceId) {
        params.spaceId = spaceId as ObjectIdString;
      } else if (projectId) {
        params.projectId = projectId as ObjectIdString;
      }

      if (isActionable) {
        params.isActionable = true;
      }

      if (filterType === "overdue") {
        params.overDue = true;
      } else if (filterType === "next7days") {
        params.next7days = true;
      } else if (filterType === "next30days") {
        params.next30days = true;
      }

      // KNOWN BUG, preserved verbatim: `requiresCounts` is not declared inside
      // `reviewsServiceRequest.value.params`, so Zod strips it, and `getReviewsList` destructures
      // `requiresCounts` from the TOP level of `value` — it never reads `params.requiresCounts`.
      // Benign: SlickGrid1 already sets `value.requiresCounts = true` on every remote fetch
      // (SlickGrid1/index.tsx:2257), so the facet counts this line intends are requested anyway.
      params.requiresCounts = true;
      params.selectedProjectIds = selectedProjectIds as ObjectIdString[] | undefined;
      params.recordStatusAction = reviewTypeFlag;

      if (customQuery) {
        return {
          ...customQuery,
          params: {
            ...(customQuery.params || {}),
            recordStatusAction: reviewTypeFlag,
          },
        };
      }

      return { params };
    };

    const closeModal = () => {
      setCreateReviewModal({ show: false });
    };

    const refreshGrid = () => {
      // `refresh` is optional on SlickGrid1's imperative handle but is always assigned by the grid;
      // `!` is erased and does not add a guard.
      dataGrid?.current?.refresh!();
    };

    const closeProperties = () => {
      setRecordProperties({ show: false, details: {} });
    };

    const handleSelectedOption = async (args: { item: { properties: { id?: string } } }) => {
      const selectedType =
        args.item.properties.id === reviewViewType.ARCHIVED_REVIEW
          ? reviewViewType.ARCHIVED_REVIEW
          : reviewViewType.ACTIVE_REVIEW;
      let handleSelect = getReviewPageUrl(selectedType, urlParams.toString(), location.pathname);
      navigate(handleSelect);
    };

    const toolbarClickHandler = (toolId: string) => {
      if (toolId === "create_review") {
        createReviewToolClicked();
      }
      if (toolId === "delete_Review") {
        handleDeleteClick();
      }
      if (toolId === "properties" || toolId === "review_properties") {
        propertiesToolClicked();
      }
    };

    const handleArchiveClick = async () => {
      setArchiveModal({
        show: true,
      });
    };

    const archiveBtnTemplate = () => (
      <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} onClick={() => handleArchiveClick()}>
        <div className="flex items-center gap-1">
          <img src={ArchiveToggleIcon} alt="" />
          <span>{reviewType === reviewViewType.ACTIVE_REVIEW ? t("Archive") : t("Unarchive")}</span>
        </div>
      </ButtonComponent>
    );

    const viewArchiveAndUnarchiveReviewTemplate = () => (
      <SelectDropDown
        select={(args) => handleSelectedOption(args)}
        items={
          reviewType === reviewViewType.ACTIVE_REVIEW
            ? [
                {
                  text: t("View Archived Reviews"),
                  id: reviewViewType.ARCHIVED_REVIEW,
                },
              ]
            : [
                {
                  text: t("View Active Reviews"),
                  id: reviewViewType.ACTIVE_REVIEW,
                },
              ]
        }
        cssClass="e-caret-hide"
        id="view_Archive_And_Unarchive_Review"
      >
        <IconComponent name="threedots" width="1.25rem" height="1.25rem" />
      </SelectDropDown>
    );

    const handleBackToActive = () => {
      const handleSelect = getReviewPageUrl(reviewViewType.ACTIVE_REVIEW, urlParams.toString(), location.pathname);
      navigate(handleSelect);
    };

    const performReviewOperation = async (operationType: string | null) => {
      setIsLoadingReview(true);
      const selectedRecords = dataGrid.current!.dataView.getAllSelectedItems();
      // `_id` is optional on the row only because `reviewsServiceResponse.result` is `z.unknown()`
      // — every review document has one. `!` is erased.
      const reviewIds = selectedRecords.map((r: ReviewsGridRow) => r._id!);

      let recordStatusAction: UpdateReviewStatusRequest["recordStatusAction"];
      let successMessage: TranslatedString;
      let errorMessage: TranslatedString;
      let modalSetter: () => void;

      switch (operationType) {
        case reviewViewType.ACTIVE_REVIEW:
          recordStatusAction = recordStatus.ARCHIVED;
          successMessage = t("The review(s) has been archived successfully!");
          errorMessage = t("The review(s) could not be archived.");
          modalSetter = () => setArchiveModal({ show: false });
          break;
        case reviewViewType.ARCHIVED_REVIEW:
          recordStatusAction = recordStatus.ACTIVE;
          successMessage = t("The review(s) has been unarchived successfully!");
          errorMessage = t("The review(s) could not be unarchived.");
          modalSetter = () => setArchiveModal({ show: false });
          break;
        case reviewViewType.DELETED_REVIEW:
          recordStatusAction = recordStatus.DELETED;
          successMessage = t("The review(s) has been deleted successfully!");
          errorMessage = t("The review(s) could not be deleted.");
          modalSetter = () => setDeleteModal({ show: false });
          break;
        default:
          throw new Error("Invalid operation type");
      }

      const operation: UpdateReviewStatusRequest = {
        reviewIds,
        recordStatusAction,
        // `projectId` comes from the URL query and is therefore `string | null`, while
        // `updateReviewStatusRequest` declares it REQUIRED. Only reachable when `archiveToggle`
        // is true, which the portal-level view (ReviewsPage, byPortal) explicitly sets to false,
        // so the null path is not reachable today. `!` is erased — a null would still be sent.
        projectId: projectId! as ObjectIdString,
      };

      try {
        const response = await updateReviewStatus(operation);
        setIsLoadingReview(false);
        if (response.data?.status) {
          dataGrid.current!.refresh!();
          dispatch(
            showToastNotification({
              content: successMessage,
              type: TOAST_TYPE_SUCCESS,
            }),
          );
        } else {
          throw new Error(response.data.errorMessage);
        }
      } catch (err: unknown) {
        dispatch(
          showToastNotification({
            content: untranslated((err as { errorMessage?: string }).errorMessage || errorMessage),
            type: TOAST_TYPE_ERROR,
          }),
        );
        Promise.reject(err);
      } finally {
        modalSetter();
      }
    };

    const toolbarForReviews = [
      ...(!isActionable && !isPortalLevel && reviewType === reviewViewType.ACTIVE_REVIEW
        ? [
            {
              name: t("Create Review"),
              id: "create_review",
              prefixIconTemplate: <FaPlus size={12} color="black" />,
            },
          ]
        : []),
      {
        id: "archive_review",
        template: archiveBtnTemplate,
        hidden: !archiveToggle,
        disabled: !hasSelection,
      },
      {
        id: "delete_Review",
        name: t("Delete"),
        hidden: !archiveToggle,
        prefixIcon: "delete",
        disabled: !hasSelection,
      },
      {
        id: "view_Archive_And_Unarchive_Review",
        template: viewArchiveAndUnarchiveReviewTemplate,
        hidden: !archiveToggle,
        rightAllign: true,
      },
    ];

    const createReviewToolClicked = () => {
      setCreateReviewModal({ show: true });
    };

    const propertiesToolClicked = () => {
      const selectedRecords = dataGrid.current!.dataView.getAllSelectedItems();
      setRecordProperties((prev: { show: boolean; details: ReviewsGridRow }) =>
        prev.show ? { show: false, details: {} } : { show: true, details: selectedRecords[0]! },
      );
    };

    const selectionEvents = () => {
      if (!dataGrid.current) return;

      const selectedRecords = dataGrid.current.dataView.getAllSelectedItems();

      if (setIsRowSelected) setIsRowSelected(selectedRecords.length > 0);
      if (selectedRecords.length !== 1) closeProperties();
      if (showOnlyGrid) return;
    };

    const handleDeleteClick = async () => {
      setDeleteModal({
        show: true,
      });
    };

    const handleOpenReviewClick = ({
      projectId,
      spaceId,
      reviewId,
      newTab,
    }: {
      projectId: string;
      spaceId: string;
      reviewId: string;
      newTab: boolean;
    }) => {
      const url = getReviewUrl(projectId, spaceId, reviewId);
      if (newTab) {
        window.open(url);
      } else {
        navigate(url);
      }
    };
    const recordClick = (args: GridClickEvent) => {
      if (args.detail.args.cell === 0) {
        return;
      }
      const rowData: ReviewsGridRow = dataGrid.current!.slickGrid.getDataItem(args.detail.args.row);
      const reviewId = rowData._id!;
      const spaceId = rowData.spaceId!;
      // KNOWN BUG, preserved verbatim: `projectData` is a denormalised lookup and is absent for a
      // review whose space has no project — this dereference then throws "Cannot read properties
      // of undefined (reading '_id')". `!` is erased; the crash is left in place.
      const projectId = rowData.projectData!._id;
      handleOpenReviewClick({ projectId, spaceId, reviewId, newTab: false });
    };

    const createdDateTemplate = (args: ReviewsGridRow) => {
      if (!args.createdDate) return "-";

      const dateFormat = convertDate(Number(args.createdDate), "dateForTooltip");
      const container = document.createElement("div");
      const root = createRoot(container);

      root.render(
        <ToolTipComponent position="bottom" content={dateFormat}>
          <span>{convertDate(Number(args.createdDate), "dateForText")}</span>
        </ToolTipComponent>,
      );

      return container;
    };

    const finishedDateTemplate = (args: ReviewsGridRow) => {
      if (!args.finishedDate) return "-";

      const dateFormat = convertDate(Number(args.finishedDate), "dateForTooltip");
      const container = document.createElement("div");
      const root = createRoot(container);

      root.render(
        <div className="inline-flex">
          <ToolTipComponent showTipPointer position="bottom" content={dateFormat}>
            <span>{convertDate(Number(args.finishedDate), "dateForText")}</span>
          </ToolTipComponent>
        </div>,
      );

      return container;
    };

    const nextActionDueDateTemplate = (args: ReviewsGridRow) => {
      if (!args.nextActionDueDate) return "-";

      const dueDateTimestamp = args.nextActionDueDate;
      const currentTimestamp = Date.now();
      const msInOneDay = 1000 * 60 * 60 * 24;

      const daysDifference = Math.ceil((dueDateTimestamp - currentTimestamp) / msInOneDay);

      const isOverdue = daysDifference < 0 || Object.is(daysDifference, -0);
      const isDueSoon = daysDifference >= 0 && daysDifference <= 3;

      const formattedDate = convertDate(dueDateTimestamp, "dateForText");

      let badgeColor = "";
      let tooltipMessage: TranslatedString | "" = "";

      if (isOverdue) {
        badgeColor = "bg-red-500";
        tooltipMessage = t("Due date exceeded by {{days}} day(s)", { days: Math.abs(daysDifference) });
      } else if (isDueSoon) {
        badgeColor = "bg-yellow-400";
        tooltipMessage = t("Due date approaching in {{days}} day(s)", { days: daysDifference });
      }

      const container = document.createElement("div");
      const root = createRoot(container);

      root.render(
        <div className="inline-flex">
          <div className="flex items-center gap-2">
            <span>{formattedDate}</span>

            {tooltipMessage && (
              <ToolTipComponent showTipPointer position="bottom" content={tooltipMessage}>
                <span
                  className={`w-5 h-5 flex items-center justify-center rounded-full text-white text-xs ${badgeColor}`}
                >
                  !
                </span>
              </ToolTipComponent>
            )}
          </div>
        </div>,
      );

      return container;
    };

    const waitingForTemplate = (args: ReviewsGridRow) => {
      const { currentReviewLevel, reviewStatus } = args;

      // `reviewStatus` is optional on the row; `includes(undefined)` is false either way. `!` is erased.
      if (["Closed", "Revoked"].includes(reviewStatus!) || !currentReviewLevel) {
        return "-";
      }

      return `${currentReviewLevel.serial} - ${currentReviewLevel.title}`;
    };

    const reviewSerialIdTemplate = (args: ReviewsGridRow) => {
      return `RE - ${args.serialId}`;
    };

    const creatorTemplate = (args: ReviewsGridRow) => {
      const container = document.createElement("div");
      renderUserTag(container, { id: args.createdBy?.userId });

      return container;
    };

    const reviewFlowTemplate = (args: ReviewsGridRow) => {
      const container = document.createElement("div");
      const root = createRoot(container);

      root.render(<div>{args.reviewFlow?.name ?? "-"}</div>);

      return container;
    };

    const projectNameTemplate = (args: ReviewsGridRow) => {
      const container = document.createElement("div");
      const root = createRoot(container);

      // KNOWN BUG, preserved verbatim: same missing-`projectData` dereference as `recordClick` —
      // absent for a review whose space has no project. `!` is erased.
      root.render(<div>{args.projectData!.description}</div>);

      return container;
    };

    const stagetemplate = (stage: string) => {
      const container = document.createElement("div");
      const root = createRoot(container);

      root.render(
        <div
          className={`flex flex-row items-center w-fit justify-start gap-2 py-[0.53125rem] px-3.5 rounded-2xl ${
            reviewStageMapper[stage]?.containerClass === "reviewStageClosed"
              ? "bg-[rgb(69_207_128_/_25%)]"
              : reviewStageMapper[stage]?.containerClass === "reviewStageRevoked"
                ? "bg-[rgb(231_85_85_/_25%)]"
                : reviewStageMapper[stage]?.containerClass === "reviewStageOpen"
                  ? "bg-[rgb(255_207_85_/_25%)]"
                  : "bg-[rgb(102_106_132_/_25%)]"
          }`}
        >
          <div
            className={`rounded-full h-3 w-3 ${
              reviewStageMapper[stage]?.indicatorClass === "reviewStageClosedIndicator"
                ? "bg-[#45cf80]"
                : reviewStageMapper[stage]?.indicatorClass === "reviewStageRevokedIndicator"
                  ? "bg-[#e75555]"
                  : reviewStageMapper[stage]?.indicatorClass === "reviewStageOpenIndicator"
                    ? "bg-[#ffcf55]"
                    : "bg-[#666a84]"
            }`}
          />
          <p className="text-sm font-semibold">{reviewStageMapper[stage]?.name}</p>
        </div>,
      );

      return container;
    };

    const reviewStageTemplate = (args: ReviewsGridRow) => {
      // `stagetemplate` takes one parameter; the second argument has always been ignored. Retyping
      // the receiver (not adding a parameter) keeps the emitted call byte-identical.
      return (stagetemplate as (stage: string, cssClass: string) => HTMLElement)(
        args.reviewStatus!,
        "reviewStageInGrid",
      );
    };

    const navigateTemplate = (args: ReviewsGridRow) => {
      const reviewId = args._id!;
      const spaceId = args.spaceId!;
      // KNOWN BUG, preserved verbatim: missing-`projectData` dereference, as above. `!` is erased.
      const projectId = args.projectData!._id;
      const container = document.createElement("div");
      const root = createRoot(container);

      root.render(
        <IconComponent
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            handleOpenReviewClick({ projectId, spaceId, reviewId, newTab: true });
          }}
          className="cursor-pointer"
          name="openInViewer"
          width="1.25rem"
          height="1.25rem"
        />,
      );

      return container;
    };

    const [reviewsGridColumns] = useState(
      getReviewsGridColumns({
        creatorTemplate,
        createdDateTemplate,
        finishedDateTemplate,
        nextActionDueDateTemplate,
        waitingForTemplate,
        reviewSerialIdTemplate,
        reviewStageTemplate,
        reviewFlowTemplate,
        projectNameTemplate,
        navigateTemplate,
        showNavigateColumn,
      }),
    );

    const closeArchiveModal = () => {
      setArchiveModal({ show: false });
    };

    const archiveModalContent = () => {
      const isArchiving = reviewType === reviewViewType.ACTIVE_REVIEW;
      const confirmationText = isArchiving
        ? t("Are you sure you want to archive the selected review(s)?")
        : t("Are you sure you want to unarchive the selected review(s)?");
      return <div>{confirmationText}</div>;
    };

    const archiveModalFooter = () => {
      const isArchiving = reviewType === reviewViewType.ACTIVE_REVIEW;
      const actionButton = isArchiving ? t("Archive") : t("Unarchive");

      return (
        <div className="flex gap-4">
          <ButtonComponent
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            onClick={() => performReviewOperation(reviewType)}
            loading={isLoadingReview}
          >
            {actionButton}
          </ButtonComponent>
          <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeArchiveModal}>
            <Text>{t("Cancel")}</Text>
          </ButtonComponent>
        </div>
      );
    };

    const deleteModalHeader = () => {
      return (
        <div className="px-6 py-4">
          <span className="text-xl font-semibold">
            <Text>{t("Delete Review(s)")}</Text>
          </span>
        </div>
      );
    };

    const deleteModalContent = () => {
      return (
        <div className="px-6">
          <Text>{t("Are you sure you want to delete the selected review(s)?")}</Text>
        </div>
      );
    };

    const deleteModalFooter = () => {
      return (
        <div className="flex justify-start px-6 py-4 gap-4">
          <ButtonComponent
            buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
            onClick={() => performReviewOperation("deleted")}
            loading={isLoadingReview}
          >
            <Text>{t("Delete")}</Text>
          </ButtonComponent>
          <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={handleDeleteModalCancel}>
            <Text>{t("Cancel")}</Text>
          </ButtonComponent>
        </div>
      );
    };

    const handleDeleteModalCancel = () => {
      setDeleteModal({ show: false });
    };

    const handleSelectedRowIdsChanged = () => {
      if (_.get(dataGrid, "current.dataView")) {
        const selected = dataGrid.current!.dataView.getAllSelectedItems();
        setHasSelection(selected.length > 0);
        setPropertiesBtnDisabled(selected.length !== 1);
      }
      selectionEvents();
    };

    const getDataGrid = () => {
      if (!reviewsQuery) return null;
      return (
        <SlickGrid1
          ref={dataGrid}
          remoteDataSource={reviewsUrl}
          query={reviewsQuery}
          // `toolbar` is declared `ToolbarItem[] | undefined`, but this grid passes `null` (which the
          // grid treats as "no toolbar") and its items carry `name` / `hidden` / `prefixIconTemplate`
          // — three props ToolBar.tsx reads but `ToolbarItem` does not declare. COMPONENT/TYPE GAP,
          // reported; the assertion is erased.
          toolbar={(showOnlyGrid ? null : toolbarForReviews) as ToolbarItem[]}
          toolbarClickHandler={toolbarClickHandler}
          columnDefinitions={reviewsGridColumns as ValidatedColumnDefinition<ReviewsGridRow>[]}
          onClick={recordClick}
          infiniteScroll
          enableColumnChooser
          onSelectedRowIdsChanged={handleSelectedRowIdsChanged}
          excelExport
          enableFilteringOption
          enableRowResize
          enableSortingOption
          enableGroupingOption
          propertiesPanel={true}
          propertiesDisabled={propertiesBtnDisabled}
          gridOptions={{ enableAutoSizeColumns: true }}
          enableViews={Boolean(projectId)}
          {...(Boolean(projectId) && {
            viewsOptions: {
              headerName: "Views",
              name: "View",
              resourceType: "REVIEWS",
              selectedLayoutKey: "REVIEWS_SELECTED_LAYOUT",
              fetchParams: {
                projectId,
              },
            },
          })}
        />
      );
    };

    return (
      <div className="flex gap-4 h-full w-full">
        {reviewType === reviewViewType.ARCHIVED_REVIEW && (
          <div className="flex flex-row items-center border-b border-blue-50 pb-4">
            <div className="pt-2 ps-2">
              <IoChevronBack size={24} className="cursor-pointer" onClick={handleBackToActive} />
            </div>
            <div className="flex flex-col ms-4 pt-2">
              <span className="mb-1">
                <Text>{t("Archived Reviews")}</Text>
              </span>
              <span className="text-sm">
                <Text>{t("List of all archived review(s) in this scope")}</Text>
              </span>
            </div>
          </div>
        )}
        {showOnlyGrid ? (
          <div className="h-full w-full overflow-hidden">{getDataGrid()}</div>
        ) : (
          <GridPageLayout
            showHeader={!!projectId && !spaceId}
            icon="reviews"
            title="Reviews"
            description="All the reviews in this project"
          >
            {getDataGrid()}
          </GridPageLayout>
        )}
        {recordProperties.show && recordProperties.details._id && (
          <ReviewProperties
            _id={recordProperties.details._id}
            closeProperties={closeProperties}
            refreshGrid={refreshGrid}
            spaceId={recordProperties.details.spaceId}
            allowFetchData
            showHeader
            accordionOrder={["details", "access", "reviewFlow"]}
          />
        )}
        {createReviewModal.show && (
          <CreateReviewModal
            spaceId={spaceId}
            refreshGrid={refreshGrid}
            closeModal={closeModal}
            // `projectId` is `string | null` from the URL query while CreateReviewModal declares it
            // `string`. Unreachable with null: the "create_review" toolbar item is only rendered when
            // `!isPortalLevel`. `!` is erased.
            projectId={projectId!}
          />
        )}
        {archiveModal.show && (
          <ModalStateDialogComponent
            isVisible={archiveModal.show}
            allowModalPadding={false}
            width="31.25rem"
            headerTitle={
              reviewType === reviewViewType.ACTIVE_REVIEW ? t("Archive Review(s)") : t("Unarchive Review(s)")
            }
            modelContent={archiveModalContent()}
            onCancel={closeArchiveModal}
            footerContent={archiveModalFooter()}
          />
        )}
        {deleteModal.show && (
          <ModalStateDialogComponent
            isVisible={deleteModal.show}
            allowModalPadding={false}
            width="31.25rem"
            headerTitle={deleteModalHeader()}
            modelContent={deleteModalContent()}
            onCancel={handleDeleteModalCancel}
            footerContent={deleteModalFooter()}
          />
        )}
      </div>
    );
  },
);
ReviewsList.displayName = "ReviewsList";

export default ReviewsList;
